import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { comics } from "@/mocks/comics";
import { useSeo } from "@/hooks/useSeo";

const demoPanels = [
  {
    bg: "from-primary-100 to-secondary-100",
    lines: ["夜色渐深，后花园的灯一盏盏亮起。", "「你怎么还在这里？」", "女孩回过身，手里的画本被风翻开。"],
  },
  {
    bg: "from-secondary-100 to-accent-100",
    lines: ["她们隔着花丛对视，谁都没有先开口。", "「……我只是想看看这些花。」", "「那，明天也一起来看吧。」"],
  },
  {
    bg: "from-accent-100 to-primary-100",
    lines: ["心跳的声音，比夜风还要清楚。", "有些话不必说出口，", "眼神已经先一步承认了一切。"],
  },
];

export default function Reader() {
  const { id, chapterId } = useParams<{ id: string; chapterId: string }>();
  const comic = useMemo(() => comics.find((item) => item.id === id), [id]);

  const chapterIndex = comic ? comic.chapters.findIndex((c) => c.id === chapterId) : -1;
  const chapter = comic && chapterIndex >= 0 ? comic.chapters[chapterIndex] : undefined;
  const prevChapter = comic && chapterIndex > 0 ? comic.chapters[chapterIndex - 1] : undefined;
  const nextChapter = comic && chapterIndex >= 0 && chapterIndex < comic.chapters.length - 1 ? comic.chapters[chapterIndex + 1] : undefined;

  useSeo({
    title: chapter
      ? `${comic?.title} ${chapter.title} - 韩漫免费漫画在线观看阅读双女主`
      : "阅读页 - 韩漫免费漫画在线观看阅读双女主",
    description: chapter
      ? `${comic?.title} ${chapter.title} 在线阅读演示，,双女主韩漫免费观看，章节内容演示中。`
      : "双女主韩漫在线阅读演示页。",
    keywords: "韩漫在线阅读,双女主漫画,漫画阅读",
    canonicalPath: `/reader/${id ?? ""}/${chapterId ?? ""}`,
  });

  if (!comic || !chapter) {
    return (
      <div className="mx-auto flex w-full max-w-[1280px] flex-col items-center px-4 py-24 text-center md:px-8">
        <i className="ri-file-unknow-line text-5xl text-foreground-300" />
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">章节不存在或已下架</h1>
        <p className="mt-3 text-sm text-foreground-600">请返回漫画详情页重新选择章节。</p>
        <Link
          to="/category"
          className="mt-6 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          浏览其他漫画
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[820px] px-4 py-8 md:px-8 md:py-12">
      <nav className="mb-6 flex flex-wrap items-center gap-2 text-sm text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-600">首页</Link>
        <i className="ri-arrow-right-s-line" />
        <Link to={`/detail/${comic.id}`} className="transition-colors hover:text-primary-600">{comic.title}</Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-900">{chapter.title}</span>
      </nav>

      <header className="mb-6 text-center">
        <h1 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">{comic.title}</h1>
        <p className="mt-2 text-sm text-primary-600">{chapter.title} · {chapter.date}</p>
      </header>

      <div className="mb-6 flex items-center justify-center gap-2 rounded-md border border-accent-300 bg-accent-50 px-4 py-3 text-center text-xs text-accent-700">
        <i className="ri-information-line text-base" />
        演示内容，待后端接口接入 · 以下为示例画面，正式章节上线后自动替换
      </div>

      <article className="flex flex-col gap-4">
        {demoPanels.map((panel, i) => (
          <div
            key={i}
            className={`relative flex min-h-[260px] flex-col items-center justify-center gap-3 rounded-lg border border-background-200 bg-gradient-to-br ${panel.bg} p-8 text-center md:min-h-[340px]`}
          >
            <span className="absolute left-3 top-3 text-xs font-medium text-foreground-400">第 {i + 1} 格</span>
            {panel.lines.map((line, j) => (
              <p
                key={j}
                className={`${
                  line.startsWith("「")
                    ? "max-w-md rounded-md bg-background-50/90 px-4 py-2 text-sm font-medium text-foreground-900"
                    : "text-sm text-foreground-700"
                }`}
              >
                {line}
              </p>
            ))}
          </div>
        ))}
      </article>

      <div className="mt-8 flex flex-col items-center justify-between gap-3 border-t border-background-200 pt-6 sm:flex-row">
        {prevChapter ? (
          <Link
            to={`/reader/${comic.id}/${prevChapter.id}`}
            className="inline-flex w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 sm:w-auto"
          >
            <i className="ri-arrow-left-s-line text-lg" /> {prevChapter.title}
          </Link>
        ) : (
          <span className="hidden sm:block" />
        )}

        <Link
          to={`/detail/${comic.id}`}
          className="inline-flex w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 sm:w-auto"
        >
          <i className="ri-list-check-2" /> 章节目录
        </Link>

        {nextChapter ? (
          <Link
            to={`/reader/${comic.id}/${nextChapter.id}`}
            className="inline-flex w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 sm:w-auto"
          >
            {nextChapter.title} <i className="ri-arrow-right-s-line text-lg" />
          </Link>
        ) : (
          <span className="hidden sm:block" />
        )}
      </div>
    </div>
  );
}